/* ============================================================
   p4s-mobile.js — phone enhancement for the homepage
   ------------------------------------------------------------
   Wraps the review cards in a .reviews__track element so the phone
   CSS can turn them into a one-card-at-a-time swipe carousel. The
   track is display:contents on desktop (see p4s-mobile.css), so the
   desktop reviews grid is unaffected at any width.

   The mobile footer accordions are native <details> elements in the
   .footer-mobile block, so they need NO JavaScript.
   No dependencies. ES5-style to match theme.js.
   ============================================================ */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  /* Reviews: move the .review-card children into a .reviews__track wrapper. */
  function buildReviewTracks() {
    var sections = document.querySelectorAll('.reviews');
    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      if (sec.querySelector('.reviews__track')) { continue; }
      var kids = sec.children;
      var cards = [];
      for (var k = 0; k < kids.length; k++) {
        if (kids[k].classList && kids[k].classList.contains('review-card')) {
          cards.push(kids[k]);
        }
      }
      if (!cards.length) { continue; }
      var track = document.createElement('div');
      track.className = 'reviews__track';
      sec.insertBefore(track, cards[0]);
      for (var c = 0; c < cards.length; c++) { track.appendChild(cards[c]); }
    }
  }

  /* Collections: give the horizontal carousel a visible, draggable scroll bar
     so it's clear the wall-art cards scroll sideways. The bar is injected after
     the .collection-grid, styled mobile-only in p4s-mobile.css, and hides itself
     when there's nothing to scroll (so desktop's static grid is unaffected). */
  function wireCollectionScrollbar(grid, bar, thumb) {
    var dragging = false;
    var startX = 0;
    var startScroll = 0;

    function maxScroll() { return grid.scrollWidth - grid.clientWidth; }

    function update() {
      var max = maxScroll();
      if (max <= 1) { bar.style.display = 'none'; return; }
      bar.style.display = '';
      var trackW = bar.clientWidth;
      var thumbW = Math.max(28, Math.round(trackW * (grid.clientWidth / grid.scrollWidth)));
      thumb.style.width = thumbW + 'px';
      var travel = trackW - thumbW;
      var frac = grid.scrollLeft / max;
      thumb.style.transform = 'translateX(' + Math.round(frac * travel) + 'px)';
    }

    function onDown(e) {
      dragging = true;
      startX = e.clientX;
      startScroll = grid.scrollLeft;
      thumb.style.cursor = 'grabbing';
      try { thumb.setPointerCapture(e.pointerId); } catch (err) {}
      e.preventDefault();
    }
    function onMove(e) {
      if (!dragging) { return; }
      var travel = bar.clientWidth - thumb.offsetWidth;
      if (travel <= 0) { return; }
      grid.scrollLeft = startScroll + ((e.clientX - startX) / travel) * maxScroll();
    }
    function onUp(e) {
      dragging = false;
      thumb.style.cursor = 'grab';
      try { thumb.releasePointerCapture(e.pointerId); } catch (err) {}
    }

    function onTrackClick(e) {
      if (e.target === thumb) { return; }
      var travel = bar.clientWidth - thumb.offsetWidth;
      if (travel <= 0) { return; }
      var pos = (e.clientX - bar.getBoundingClientRect().left) - thumb.offsetWidth / 2;
      if (pos < 0) { pos = 0; }
      if (pos > travel) { pos = travel; }
      grid.scrollLeft = (pos / travel) * maxScroll();
    }

    thumb.addEventListener('pointerdown', onDown);
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
    bar.addEventListener('click', onTrackClick);
    grid.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    update();
  }

  function buildCollectionScrollbars() {
    var grids = document.querySelectorAll('.collections .collection-grid');
    for (var i = 0; i < grids.length; i++) {
      var grid = grids[i];
      if (grid.getAttribute('data-p4s-bar') === '1') { continue; }
      grid.setAttribute('data-p4s-bar', '1');
      var bar = document.createElement('div');
      bar.className = 'collection-scrollbar';
      var thumb = document.createElement('div');
      thumb.className = 'collection-scrollbar__thumb';
      bar.appendChild(thumb);
      if (grid.nextSibling) { grid.parentNode.insertBefore(bar, grid.nextSibling); }
      else { grid.parentNode.appendChild(bar); }
      wireCollectionScrollbar(grid, bar, thumb);
    }
  }

  /* Reviews carousel: prev/next arrows + position dots (mobile only). Lets the
     one-at-a-time testimonial slider be moved left/right by tap, and shows which
     review is in view. Styled in p4s-mobile.css; hidden on desktop. */
  function wireReviewNav(track, cards, prevBtn, nextBtn) {
    // Advance by exactly ONE card per click, left-aligned — the same motion as
    // the live store's Swiper (one card slides in from the right, the row stays
    // aligned). cardPitch = one card's width + the gap to the next card.
    function cardPitch() {
      if (cards.length >= 2) {
        return Math.round(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left);
      }
      return Math.round(cards[0].getBoundingClientRect().width);
    }
    function move(dir) {
      var by = dir * cardPitch();
      try { track.scrollBy({ left: by, behavior: 'smooth' }); }
      catch (err) { track.scrollLeft += by; }
    }
    prevBtn.addEventListener('click', function () { move(-1); });
    nextBtn.addEventListener('click', function () { move(1); });
  }

  function buildReviewNav() {
    var sections = document.querySelectorAll('.reviews');
    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      var track = sec.querySelector('.reviews__track');
      if (!track) { continue; }
      if (sec.getAttribute('data-p4s-nav') === '1') { continue; }
      var cards = track.querySelectorAll('.review-card');
      if (cards.length < 2) { continue; }
      sec.setAttribute('data-p4s-nav', '1');

      var nav = document.createElement('div');
      nav.className = 'reviews-nav';
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'reviews-nav__arrow';
      prev.setAttribute('aria-label', 'Previous review');
      prev.innerHTML = '&#8249;';
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'reviews-nav__arrow';
      next.setAttribute('aria-label', 'Next review');
      next.innerHTML = '&#8250;';
      nav.appendChild(prev);
      nav.appendChild(next);
      if (track.nextSibling) { track.parentNode.insertBefore(nav, track.nextSibling); }
      else { track.parentNode.appendChild(nav); }
      wireReviewNav(track, cards, prev, next);
    }
  }

  ready(function () {
    buildReviewTracks();
    buildCollectionScrollbars();
    buildReviewNav();
  });
})();
